
import React from "react";
import { Part, PartCategory, PartFilter, PartSubcategory } from "@/data/parts";
import { TableCell } from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { ColumnId, SortState } from "./PartTableHeader";
import NameCell from "./cells/NameCell";
import CategoryCell from "./cells/CategoryCell";
import SubcategoryCell from "./cells/SubcategoryCell";
import RatingCell from "./cells/RatingCell";
import ComplexityCell from "./cells/ComplexityCell";
import FiltersCell from "./cells/FiltersCell";
import ActionsCell from "./cells/ActionsCell";

interface PartTableCellProps {
  part: Part;
  columnId: ColumnId;
  sortState: SortState;
  activeFilters: PartFilter[];
  onCategoryClick: (category: PartCategory) => void;
  onSubcategoryClick: (subcategory: PartSubcategory) => void;
  onFilterClick: (filter: PartFilter) => void;
  onRatingSort?: (columnId: ColumnId) => void;
}

const PartTableCell = ({
  part,
  columnId,
  sortState,
  activeFilters,
  onCategoryClick,
  onSubcategoryClick,
  onFilterClick,
  onRatingSort,
}: PartTableCellProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // Function to navigate to part details
  const goToPartDetails = () => {
    navigate(`/parts/${part.id}`);
  };
  
  // Render cell content based on column ID
  const renderCellContent = () => {
    switch (columnId) {
      case "name":
        return (
          <NameCell
            part={part}
            goToPartDetails={goToPartDetails}
            onCategoryClick={onCategoryClick}
            onSubcategoryClick={onSubcategoryClick}
            sortState={sortState}
            onRatingSort={onRatingSort}
          />
        );
      case "category":
        return !isMobile ? (
          <CategoryCell 
            category={part.category}
            onClick={onCategoryClick}
          />
        ) : null;
      case "type":
        return !isMobile ? (
          <SubcategoryCell 
            subcategory={part.subcategory}
            onClick={onSubcategoryClick}
          />
        ) : null;
      case "rating":
        return !isMobile && part.rating > 0 ? (
          <RatingCell 
            rating={part.rating}
            sortState={sortState}
            columnId={columnId}
          />
        ) : null;
      case "complexity":
        return !isMobile ? (
          <ComplexityCell
            requiresRoot={part.requiresRoot}
            requiresWiring={part.requiresWiring}
            requiresPrinting={part.requiresPrinting}
            requiresTools={part.requiresTools}
            requiresPurchase={part.requiresPurchase}
          />
        ) : null;
      case "filters":
        return !isMobile ? (
          <FiltersCell
            filters={part.filters}
            activeFilters={activeFilters}
            onFilterClick={onFilterClick}
          />
        ) : null;
      case "actions":
        return !isMobile ? (
          <ActionsCell onClick={goToPartDetails} />
        ) : null;
      default:
        return null;
    }
  };
  
  const cellContent = renderCellContent();
  
  // Don't render the cell at all on mobile if it has no content
  if (isMobile && !cellContent) {
    return null;
  }
  
  return (
    <TableCell 
      className={`${columnId === 'name' ? 'py-3' : ''}`}
    >
      {cellContent}
    </TableCell>
  );
};

export default PartTableCell;
